import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";

import { RootState } from "../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  PostDataSuccessAction,
  PostDataFailureAction,
  fetchData,
  postData,
} from "../../store/actions";
import { useCustomToast } from "../../components/Methods/SnackBar";
import {
  FEEDBACK_TEMPLATES_API,
  FEEDBACK_REQUESTS_API,
  viewActiveUsers,
  projectsUrl,
} from "../../Service/Apis";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Minimal representation of a system user used to populate the employee dropdown. */
interface UserOption {
  id: string | number;
  first_name?: string;
  last_name?: string;
  email?: string;
  /** Internal employee code displayed alongside the name for disambiguation. */
  employee_code?: string;
}

/** Subset of project data needed to associate a feedback request with a project. */
interface ProjectOption {
  project_id: string;
  project_name: string;
  /** Only active projects are shown in the dropdown; inactive ones are filtered out. */
  is_active?: boolean;
}

/** Feedback template option returned by the API. Only active templates are surfaced to HR/managers. */
interface TemplateOption {
  template_id: string;
  name: string;
  is_active?: boolean;
}

/** Controlled form state for the "New Client Feedback Request" dialog. */
interface FormValues {
  employee: string;
  /** Optional — a feedback request is valid even without a linked project. */
  project: string;
  client_name: string;
  client_email: string;
  template: string;
  /**
   * Optional custom text the HR/Manager types as extra email body content.
   * It is appended after the permanent opening sentence in the sent email.
   */
  email_body: string;
}

/** Field-level validation error messages shown inline beneath each form control. */
interface FormErrors {
  employee?: string;
  client_name?: string;
  client_email?: string;
  template?: string;
}

/** Props accepted by the CreateFeedbackRequest modal dialog. */
interface Props {
  /** Whether the dialog is currently open. */
  open: boolean;
  /** Called when the user dismisses the dialog without submitting. */
  onClose: () => void;
  /** Called after a feedback request is successfully created so the parent can refresh its list. */
  onCreated: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Modal dialog that lets HR, Admin, or Manager staff create a new client
 * feedback request. On submission, the backend generates a unique token,
 * persists the request, and dispatches an invitation email to the client.
 *
 * Dropdown data (employees, active projects, active templates) is fetched
 * fresh every time the dialog opens so stale data is never shown.
 */
const CreateFeedbackRequest: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const [values, setValues] = useState<FormValues>({
    employee: "",
    project: "",
    client_name: "",
    client_email: "",
    template: "",
    email_body: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [users, setUsers] = useState<UserOption[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState<boolean>(false);

  const showToast = useCustomToast();
  const selectedRole = useSelector((state: RootState) => state.auth.selectedRole);

  const dispatch: ThunkDispatch<
    RootState,
    null,
    | FetchDataSuccessAction
    | FetchDataFailureAction
    | PostDataSuccessAction
    | PostDataFailureAction
  > = useDispatch();

  // Load dropdown data when dialog opens
  useEffect(() => {
    if (!open) return;
    setValues({ employee: "", project: "", client_name: "", client_email: "", template: "", email_body: "" });
    setErrors({});

    const loadOptions = async () => {
      setLoadingOptions(true);
      try {
        const [usersData, projectsData, templatesData] = await Promise.all([
          dispatch(fetchData(viewActiveUsers)),
          dispatch(fetchData(`${projectsUrl}?role=${selectedRole}`)),
          dispatch(fetchData(FEEDBACK_TEMPLATES_API)),
        ]);

        const allUsers: UserOption[] = Array.isArray(usersData) ? usersData : usersData?.results ?? [];
        setUsers(allUsers);

        const allProjects: ProjectOption[] = Array.isArray(projectsData) ? projectsData : projectsData?.results ?? [];
        // Only show active projects
        setProjects(allProjects.filter((p) => p.is_active !== false));

        const allTemplates: TemplateOption[] = Array.isArray(templatesData) ? templatesData : templatesData?.results ?? [];
        // Only show active templates
        setTemplates(allTemplates.filter((t) => t.is_active !== false));
      } catch {
        showToast("Failed to load options.", "error", false);
      } finally {
        setLoadingOptions(false);
      }
    };

    loadOptions();
  }, [open, dispatch, showToast]);

  /**
   * Builds a human-readable label for a user dropdown option.
   * Falls back gracefully: full name → email → numeric id.
   * Appends the employee code in parentheses when available so that
   * users with identical names can be distinguished.
   */
  const getUserLabel = (u: UserOption): string => {
    const name = [u.first_name, u.last_name].filter(Boolean).join(" ");
    const label = name || u.email || String(u.id);
    return u.employee_code ? `${label} (${u.employee_code})` : label;
  };

  /**
   * Runs client-side validation before the form is submitted.
   * Populates `errors` state with per-field messages and returns
   * `false` if any required field is missing or malformed.
   */
  const validate = (): boolean => {
    const errs: FormErrors = {};
    if (!values.employee) errs.employee = "Please select an employee.";
    if (!values.client_name.trim()) errs.client_name = "Client name is required.";
    if (!values.client_email.trim()) {
      errs.client_email = "Client email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.client_email)) {
      errs.client_email = "Enter a valid email address.";
    }
    if (!values.template) errs.template = "Please select a template.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  /**
   * Posts the new feedback request to the API. Project is sent as `null`
   * when omitted so the backend knows it is intentionally unset rather than
   * missing from the payload. On success, notifies the parent via `onCreated`.
   */
  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await dispatch(
        postData(FEEDBACK_REQUESTS_API, {
          employee: values.employee,
          project: values.project || null,
          client_name: values.client_name.trim(),
          client_email: values.client_email.trim(),
          template: values.template,
          email_body: values.email_body.trim(),
        } as any)
      );
      showToast("Feedback request created and email sent.", "success", false);
      onCreated();
    } catch {
      showToast("Failed to create feedback request.", "error", false);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Updates a single form field and clears its validation error so the user
   * gets immediate visual feedback as soon as they start correcting the input.
   */
  const handleChange = (field: keyof FormValues, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { minHeight: "75vh" } }}>
      <DialogTitle>New Client Feedback Request</DialogTitle>

      <DialogContent dividers>
        {loadingOptions ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
            {/* Employee */}
            <FormControl size="small" error={!!errors.employee}>
              <InputLabel>Select Employee Name *</InputLabel>
              <Select
                label="Select Employee Name *"
                value={values.employee}
                onChange={(e) => handleChange("employee", e.target.value)}
              >
                {users.map((u) => (
                  <MenuItem key={String(u.id)} value={String(u.id)}>
                    {getUserLabel(u)}
                  </MenuItem>
                ))}
              </Select>
              {errors.employee && (
                <FormHelperText>{errors.employee}</FormHelperText>
              )}
            </FormControl>

            {/* Project (optional) */}
            <FormControl size="small">
              <InputLabel>Project (optional)</InputLabel>
              <Select
                label="Project (optional)"
                value={values.project}
                onChange={(e) => handleChange("project", e.target.value)}
              >
                <MenuItem value="">— None —</MenuItem>
                {projects.map((p) => (
                  <MenuItem key={p.project_id} value={p.project_id}>
                    {p.project_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Client Name */}
            <TextField
              label="Enter Client Name *"
              placeholder="Enter Client Name"
              size="small"
              value={values.client_name}
              onChange={(e) => handleChange("client_name", e.target.value)}
              error={!!errors.client_name}
              helperText={errors.client_name}
            />

            {/* Client Email */}
            <TextField
              label="Enter Client Email-ID *"
              placeholder="Enter Client Email-ID"
              size="small"
              type="email"
              value={values.client_email}
              onChange={(e) => handleChange("client_email", e.target.value)}
              error={!!errors.client_email}
              helperText={errors.client_email}
            />

            {/* Template */}
            <FormControl size="small" error={!!errors.template}>
              <InputLabel>Select Feedback Template *</InputLabel>
              <Select
                label="Select Feedback Template *"
                value={values.template}
                onChange={(e) => handleChange("template", e.target.value)}
              >
                {templates.map((t) => (
                  <MenuItem key={t.template_id} value={t.template_id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.template && (
                <FormHelperText>{errors.template}</FormHelperText>
              )}
            </FormControl>

            {/* Dynamic Email Body */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                Email Body (optional)
              </Typography>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                {/* Permanent prefix + editable continuation — seamless single block */}
                <Box sx={{ px: 1.5, pt: 1, backgroundColor: "action.hover" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    We value your opinion and would appreciate a few minutes of your time to share feedback.
                  </Typography>
                </Box>
                <TextField
                  placeholder="Continue writing here… (optional)"
                  multiline
                  minRows={3}
                  maxRows={8}
                  fullWidth
                  size="small"
                  value={values.email_body}
                  onChange={(e) => handleChange("email_body", e.target.value)}
                  InputProps={{ disableUnderline: true }}
                  variant="standard"
                  sx={{
                    "& .MuiInputBase-root": { px: 1.5, pt: 0.5, pb: 1 },
                  }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                The grey text above is always included in the email. Your text will appear right after it.
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || loadingOptions}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitting ? "Sending…" : "Create & Send Email"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateFeedbackRequest;
